import React, { memo } from 'react';
import { Box } from '@mui/material';
import type { DailyShiftSlotGrouped } from '../../../../domain/dailyShiftSlot';
import { DEFAULT_DISPLAY_TIME_SLOTS } from '../../../../domain/dailyShiftSlot';
import {
  DayCell,
  DateLabel,
  HolidayLabel,
  TimeSlotItem,
  MoreButton,
} from '../MonthlyShiftSlotSetup.styles';

interface CalendarDayCellProps {
  day: DailyShiftSlotGrouped | null;
  onClick?: (day: DailyShiftSlotGrouped) => void;
}

/**
 * カレンダー日付セルコンポーネント
 *
 * - 日付表示
 * - 曜日による色分け（日曜: 赤、土曜: 青）
 * - 祝日表示
 * - デフォルト3時間帯表示（07:00〜、08:00〜、09:00〜）
 * - 「もっと」ボタン
 */
const CalendarDayCell: React.FC<CalendarDayCellProps> = memo(({ day, onClick }) => {
  if (!day) {
    return <DayCell $isEmpty />;
  }

  const isSunday = day.dayOfWeek === 0;
  const isSaturday = day.dayOfWeek === 6;
  const dateNumber = parseInt(day.date.split('-')[2], 10);

  // デフォルト表示用の3時間帯を取得
  const displaySlots = day.slots.filter(slot =>
    DEFAULT_DISPLAY_TIME_SLOTS.some(
      def => def.start === slot.startTime && def.end === slot.endTime
    )
  );

  const remainingCount = day.slots.length - displaySlots.length;

  const handleClick = () => {
    if (onClick) {
      onClick(day);
    }
  };

  return (
    <DayCell
      $isSunday={isSunday}
      $isSaturday={isSaturday}
      $isHoliday={day.isHoliday}
      onClick={handleClick}
    >
      <DateLabel
        $isSunday={isSunday}
        $isSaturday={isSaturday}
        $isHoliday={day.isHoliday}
      >
        {dateNumber}
      </DateLabel>

      {day.isHoliday && day.holidayName && (
        <HolidayLabel>{day.holidayName}</HolidayLabel>
      )}

      <Box>
        {displaySlots.map(slot => (
          <TimeSlotItem key={slot.id}>
            <span>{slot.startTime}〜</span>
            <span>募{slot.recruitmentSlots}/確{slot.confirmedSlots}</span>
          </TimeSlotItem>
        ))}

        {remainingCount > 0 && (
          <MoreButton>
            +{remainingCount} もっと見る
          </MoreButton>
        )}
      </Box>
    </DayCell>
  );
});

CalendarDayCell.displayName = 'CalendarDayCell';

export default CalendarDayCell;
